import { authHeader, getToken, clearToken } from "./auth";
import { loginPathFor, withNext } from "./authRedirect";
import { isActiveLocale } from "@/i18n/config";

const BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Thrown by every failed backend call. `message` is the raw response body (the
// contract `parseApiError` reads); `status` lets a caller tell a 429 or a 5xx
// from a coded 4xx without parsing the body (F35).
export class ApiRequestError extends Error {
  status: number;
  constructor(status: number, body: string) {
    super(body);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

// Shared failure path for every backend call (JSON or streamed): redirects on
// a dead token / a pending consent version, then throws the raw body.
async function failResponse(res: Response): Promise<never> {
  // An expired/invalid JWT on an authenticated request: drop the dead token
  // and bounce to login so the user isn't left staring at a cryptic error.
  // Guarded by getToken() so a 401 on the login form (wrong password, no
  // token yet) keeps its error message instead of redirecting.
  // F18: eğitmen alanında eğitmen girişine, ve her iki kapı `?next=` ile geldiği
  // sayfaya geri döner (rapor ortasında düşen öğrenci URL'sini kaybetmez).
  if (res.status === 401 && getToken() && typeof window !== "undefined") {
    clearToken();
    const here = window.location.pathname + window.location.search;
    // Kasıtlı tam yükleme: ölü token'da bellekteki her state (önbellek, akış,
    // ref'ler) sıfırlanmalı; router.push bunu yapmaz.
    window.location.href = withNext(loginPathFor(here), here);
  }
  const body = await res.text();
  // Sürümlü hukuki onay kapısı: backend, metin sürümü artınca öğrenciye dönük
  // uçlara 403 + `consent_required` döner. Kullanıcı hangi sayfada olursa olsun
  // onay ekranına alınır — kapı frontend'de değil backend'de, bu yalnızca
  // kullanıcıyı çıkmaza düşürmemek için.
  if (res.status === 403 && typeof window !== "undefined"
      && body.includes("consent_required")
      && window.location.pathname !== "/consent") {
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- aynı gerekçe: onay kapısına tam yüklemeyle gidilir.
    window.location.href = "/consent";
  }
  throw new ApiRequestError(res.status, body);
}

// next-intl'in cookie tabanlı locale'i (AppShell dil seçici yazar); SSR'da "en".
// Kapalı bir dilin cookie'si İngilizce gider: arayüz de o cookie'yle İngilizce açılır
// (`i18n/request.ts`), backend'in üreteceği metin arayüzle aynı dilde kalsın.
const currentLocale = (): string => {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|; )NEXT_LOCALE=([^;]+)/);
  const raw = m ? decodeURIComponent(m[1]) : "en";
  return isActiveLocale(raw) ? raw : "en";
};

// Backend, kodlu hataların `message` alanını bu başlığın diline çevirir
// (`errors.api_error_handler`); başlık yoksa Accept-Language, o da yoksa İngilizce.
const localeHeader = (): Record<string, string> => ({ "X-Locale": currentLocale() });

// Birden çok kohortta yetkisi olan eğitmen (ör. kendi kohortunun sahibi + başka
// bir kohortun asistanı) hangi kohorta baktığını bu başlıkla söyler. Seçim
// tarayıcıda tutulur (kabuktaki kohort seçici yazar); sunucu yalnızca kişinin zaten
// erişebildiği kohortlar arasından seçer, başlıkla yetki genişlemez.
export const INSTRUCTOR_COHORT_KEY = "pp_instructor_cohort";
const cohortHeader = (path: string): Record<string, string> => {
  if (typeof window === "undefined" || !path.startsWith("/instructor/")) return {};
  const id = window.localStorage.getItem(INSTRUCTOR_COHORT_KEY);
  return id && /^\d+$/.test(id) ? { "X-Instructor-Cohort": id } : {};
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeader(), ...localeHeader(), ...cohortHeader(path), ...init?.headers },
  });
  if (!res.ok) await failResponse(res);
  return res.json();
}

// Dosya indiren GET'ler (ör. eğitmen panelinin Excel dökümü): aynı kimlik ve
// hata yolu, gövde JSON değil Blob.
async function requestBlob(path: string): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, { headers: { ...authHeader(), ...localeHeader(), ...cohortHeader(path) } });
  if (!res.ok) await failResponse(res);
  return res.blob();
}

export type MessageReply = {
  role: string;
  content: string;
  timestamp: string;
  session_ended_by_patient: boolean;
  session_ended_by_limit: boolean;
  student_message_count: number;
  message_cap: number;
};

// Sesli turun yanıtı: metin turuyla aynı gövde + kaç cümle seslendirildi.
export type VoiceReply = MessageReply & { audio_chunks: number };

// Bu seansta ses var mı. `locale` sürecin KİLİTLİ dili (Karar 13/20), öğrencinin
// güncel arayüz dili değil: hasta süreç boyunca tek sesle konuşur.
// `requires_paid` YALNIZCA engel tier'sa doğrudur: sesi olmayan bir dilde ya da
// entegrasyon kapalıyken ücretli plana yönlendirmek, satın alınca açılmayacak
// bir şeyi satmak olurdu.
export type VoiceAvailability = { available: boolean; requires_paid: boolean; locale: string };

// One patient turn over server-sent events: `onDelta` receives the visible text
// as it is produced (the first word lands at time-to-first-token, ~1 s), and
// the promise resolves with the same payload the blocking endpoint returns —
// callers replace the streamed bubble with its `content`, which carries the
// final whitespace tidy-up. An `error` frame means nothing was persisted.
// `signal` (F8): sayfa turun ortasında terk edilirse çağıran akışı iptal eder;
// okuyucu boşalmaya devam etmez ve sökülmüş ağaca `setState` düşmez. İptal
// `AbortError` olarak yükselir; sunucu turu yine de yazmış olabilir, çağıran
// `getSession` ile uzlaştırır.
type SseEvent = { type?: string } & Record<string, unknown>;

// `data:` başına tek JSON, çerçeveler boş satırla ayrılır. Metin turu ve sesli
// tur aynı okuyucuyu kullanır: ayrıştırmayı iki yere yazmak, birinde düzeltilen
// bir hatanın diğerinde kalması demekti.
async function readSse(res: Response, onEvent: (event: SseEvent) => void): Promise<void> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done: closed } = await reader.read();
    if (closed) break;
    buffer += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const line = frame.split("\n").find((l) => l.startsWith("data: "));
      if (line) onEvent(JSON.parse(line.slice(6)) as SseEvent);
    }
  }
}

// Hata çerçevesi HTTP hatasıyla aynı zarfa sarılır ki `parseApiError` koda
// dallanabilsin (`ai_unavailable` / `voice_unavailable` / `session_not_found`).
function sseError(event: SseEvent): Error {
  return new Error(JSON.stringify({ detail: { code: event.code ?? "", message: event.detail } }));
}

async function streamMessage(
  sessionId: number,
  content: string,
  onDelta: (text: string) => void,
  signal?: AbortSignal,
): Promise<MessageReply> {
  const res = await fetch(`${BASE}/sessions/${sessionId}/message/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader(), ...localeHeader() },
    body: JSON.stringify({ content }),
    signal,
  });
  if (!res.ok || !res.body) await failResponse(res);
  let done: MessageReply | null = null;
  await readSse(res, (event) => {
    if (event.type === "delta") onDelta(String(event.text ?? ""));
    else if (event.type === "done") done = event as unknown as MessageReply;
    else if (event.type === "error") throw sseError(event);
  });
  if (!done) throw new Error("stream ended without a reply");
  return done;
}

// Bir sesli tur: bas-konuş parçası yükleniyor, hastanın sesi CÜMLE CÜMLE ve her
// cümle de PARÇA PARÇA geri geliyor (`onAudio`: n. cümlenin ~100 ms'lik ham
// PCM'i, `onAudioEnd`: n. cümle bitti). İlk hece, LLM daha yazarken ve cümle
// daha seslendirilirken çalar; tüm yanıtı beklemek 4-5 saniye ederdi.
//
// `done.audio_chunks === 0` = tur yazıldı ama hiç ses üretilemedi. Çağıran bunu
// sessizce yutmaz: öğrenci hastanın cevabını hiç duymamış olur, metin moduna
// geçilir (ses Kararı 8'in istisnası değil, erişilebilirlik gereği).
async function streamVoiceMessage(
  sessionId: number,
  audio: Blob,
  onAudio: (seq: number, data: string, rate: number) => void,
  onAudioEnd: (seq: number) => void,
  signal?: AbortSignal,
): Promise<VoiceReply> {
  const res = await fetch(`${BASE}/sessions/${sessionId}/message/voice`, {
    method: "POST",
    // Gövde ham baytlar, multipart DEĞİL: multipart ayrıştırıcısı büyük bir
    // parçayı sunucuda geçici bir DOSYAYA taşıyor ve gizlilik metni sesin hiçbir
    // aşamada saklanmadığını söylüyor. Biçim bu başlıkta gider.
    headers: {
      ...authHeader(), ...localeHeader(),
      "Content-Type": audio.type || "application/octet-stream",
    },
    body: audio,
    signal,
  });
  if (!res.ok || !res.body) await failResponse(res);
  let done: VoiceReply | null = null;
  await readSse(res, (event) => {
    if (event.type === "audio") onAudio(Number(event.seq), String(event.data ?? ""), Number(event.rate));
    else if (event.type === "audio_end") onAudioEnd(Number(event.seq));
    else if (event.type === "done") done = event as unknown as VoiceReply;
    else if (event.type === "error") throw sseError(event);
  });
  if (!done) throw new Error("voice stream ended without a reply");
  return done;
}

// Errors thrown by `request` carry the raw response body as their message. Our
// backend returns FastAPI `detail` either as a plain string or as a structured
// {code, message} object. This normalises both so callers can branch on `code`
// (e.g. "invalid_api_key") and always have a human-readable `message`.
// `extra` carries the remaining fields of a structured detail (counts, limits, ids)
// so a caller can translate the message itself instead of printing the server's
// wording — which is Turkish, and therefore wrong for every other locale.
// Two response families carry no `code` of their own and are normalised here:
// a Pydantic 422 (`detail` is an array of {loc, msg}) becomes `validation_error`
// with the first message, and a 429 whose body could not be parsed becomes
// `rate_limited` (the backend's own 429 handler already sends that code).
export function parseApiError(
  e: unknown,
): { code: string; message: string; extra: Record<string, unknown>; status: number } {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  const status = e instanceof ApiRequestError ? e.status : 0;
  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;
    if (Array.isArray(detail)) {
      const first = detail[0] as Record<string, unknown> | undefined;
      const msg = first && typeof first.msg === "string" ? first.msg : raw;
      return { code: "validation_error", message: msg, extra: {}, status };
    }
    if (detail && typeof detail === "object") {
      const { code, message, ...extra } = detail as Record<string, unknown>;
      return {
        code: typeof code === "string" ? code : "",
        message: typeof message === "string" ? message : raw,
        extra,
        status,
      };
    }
    return { code: "", message: typeof detail === "string" ? detail : raw, extra: {}, status };
  } catch {
    return { code: status === 429 ? "rate_limited" : "", message: raw, extra: {}, status };
  }
}

// Auth forms (login / register / instructor login): the backend already
// translates every coded error's `message` into the request locale (X-Locale),
// so anything with a code that the form does not word itself is shown as-is
// (rate limit, revoked token, weak password...). No code means the request
// never reached a coded handler (network, 5xx, HTML from the proxy): that is
// never "wrong password", it is `fallback` (F36).
export function authErrorText(
  e: unknown,
  own: Record<string, string>,
  fallback: string,
): string {
  const { code, message } = parseApiError(e);
  if (code && own[code]) return own[code];
  if (code && message && message !== code) return message;
  return fallback;
}

// `translations` is {locale: presenting complaint} and only carries the locales that
// actually have one — use `localizedComplaint()` rather than indexing it directly, so
// a missing language falls back to the English original instead of rendering empty.
export type CaseTranslations = Record<string, string>;
// Karar 41: `referral_reasons` de translations desenidir ({locale: kısa gelme
// sebebi}); `referral_reason` taban metin. İkisi de boşsa (kod öncesi eski vaka)
// kart o satırı hiç çizmez. `difficulty_level` legacy: API hâlâ yolluyor, ürün okumuyor.
// Faz 4: canlı kohort ödevi. Doluysa kart rozet gösterir; `reset_allowed=false`
// eğitmenin süreç sıfırlamayı kapattığı anlamına gelir (sunucu 403 reset_locked).
export type CaseAssignment = { id: number; title: string | null; instructions?: string | null; reset_allowed: boolean; ends_at: string | null };
// F3: `completed_sessions` / `last_session_at` canlı süreçteki tamamlanmış seans
// sayısı ve sonuncusunun bitişi; dashboard "devam eden danışanlar"ı buradan kurar.
export type Case = { id: number; name: string; names?: CaseTranslations; locked_locale?: string | null; age: number; presenting_complaint: string; presenting_complaint_tr?: string | null; translations?: CaseTranslations; referral_reason?: string | null; referral_reasons?: CaseTranslations; difficulty_level: string; is_custom?: boolean; assignment?: CaseAssignment | null; completed_sessions?: number; last_session_at?: string | null };

export function localizedReferralReason(
  source: { referral_reason?: string | null; referral_reasons?: CaseTranslations | null },
  locale: string,
): string | null {
  return source.referral_reasons?.[locale] || source.referral_reason || null;
}

export function localizedComplaint(
  source: { presenting_complaint: string; translations?: CaseTranslations | null },
  locale: string,
): string {
  return source.translations?.[locale] || source.presenting_complaint;
}
// Karar 26: `name` İngilizce tabandır; öğrenci kendi arayüz dilinin adını
// `names` haritasından görür (Aylin/Alice/Jonas aynı hastadır).
export function localizedName(
  source: { name: string; names?: CaseTranslations | null; locked_locale?: string | null },
  locale: string,
): string {
  // Karar 20: devam eden bir sürecin adı ilk seansta kilitlendi. Kilit varsa
  // arayüz dili DEĞİL o dil kazanır — yoksa kartta Alice yazar, içeride Aylin
  // çıkar ve kart tutamayacağı bir söz vermiş olur.
  const pick = source.locked_locale || locale;
  return source.names?.[pick] || source.names?.[locale] || source.names?.en || source.name;
}
export type CueItem = { cue: string; significance: string; easy_to_miss_because: string };
// Karar 9/41: üretim hedef-tabanlı — klinik alanlar (zorluk dahil) istekten kalktı.
export type CreateCaseRequest = {
  name?: string; age?: number; gender?: "female" | "male"; presenting_complaint: string;
  locale?: string;
};
// "hint" = a mid-session supervisor whisper the student requested; it lives in the
// transcript so its position is preserved, but it is never patient/student speech.
export type TranscriptMessage = { role: "user" | "assistant" | "hint"; content: string; timestamp: string };
// Supervisor evaluation runs server-side in the background. "none" = never started
// (or the server restarted mid-run) — the caller should (re-)POST /evaluate.
// "too_short" = the session carried too few therapist turns to supervise; terminal,
// retrying never helps.
export type EvaluationStatus = { status: "ready" | "running" | "failed" | "none" | "too_short" };
// Supervisor reports the student has never opened, grouped per patient case, so the
// dashboard can badge every card from one request. Only sessions with a written
// report appear here (too-short / failed evaluations are not "unread").
export type UnreadReports = {
  total: number;
  cases: { patient_case_id: number; unread: number; latest_session_id: number }[];
};
// ── Süpervizör raporu (F10: API sınırında TEK tip) ────────────────────────────
// Backend `progress_service.public_report`'un öğrenciye açtığı şekil. Eski raporlar
// bazı alanları taşımaz (rubric v1: `skill_items`/`anchors` yok; `approach_feedback`
// modalite geri bildiriminden önce yok), o yüzden onlar opsiyonel. `state_delta`
// sunucuda sıyrılır, burada hiç yoktur.
export type ReportMissedCue = { cue: string; when: string; why_it_mattered: string };
export type ReportCouldBeBetter = { moment: string; why: string };
export type ReportSkillMetric = { score: number | null; note?: string; observed?: boolean };
export type ReportSkillItem = { band?: number; score?: number | null; note?: string; observed?: boolean };
// Bulgunun tur bağı: tek tur, iki uçlu bulguda liste, bağsızsa null. Eski raporlar
// tek sayı taşır; `lib/reportAnchors.resolveAnchors` hepsini listeye çevirir.
export type ReportAnchor = number | null | undefined | (number | null)[];
export type ReportAnchors = {
  what_went_well?: ReportAnchor[];
  what_could_be_better?: ReportAnchor[];
  missed_cues?: ReportAnchor[];
  skill_items?: Record<string, ReportAnchor>;
};
export type ScoreReviewChange = { skill: SkillKey; from: number | null; to: number; reason: string | null };
export type ScoreReview = {
  // pending: onay yok · approved: puanlar aynen onaylandı · changed: en az bir beceri değişti
  status: "pending" | "approved" | "changed";
  reviewer_name?: string | null;
  approved_at?: string | null;
  original_overall?: number | null;
  original_skills?: Partial<Record<SkillKey, number | null>>;
  overall?: number | null;
  changes?: ScoreReviewChange[];
};
export type StudentReminderNotice = { id: number; sender_name: string | null; cohort_name: string; created_at: string | null };
export type SupervisorReport = {
  what_went_well: string[];
  what_could_be_better: ReportCouldBeBetter[];
  missed_cues: ReportMissedCue[];
  what_to_explore_further: string[];
  focus_for_next_session: string[];
  approach_feedback?: string;
  skill_metrics?: Record<string, ReportSkillMetric>;
  skill_items?: Record<string, ReportSkillItem>;
  // `progress_service.attach_metrics_summary` ekler: seans skoru + önceki seansla fark.
  metrics_summary?: import("@/components/SessionMetrics").MetricsSummary;
  anchors?: ReportAnchors;
  rubric_version?: number;
  // Karar 47 (dalga 9): terapi ekolü çipleri. Eski raporlarda yok.
  therapy_models?: { method: string; label: string; family: string; family_label: string; why_not?: string | null }[];
  // Seans öncesi niyet (değerlendirildiği haliyle) ve süpervizörün ona bakışı. Niyetsiz seansta yok.
  intent?: SessionIntent & { method_label?: string };
  intent_review?: IntentReview;
  // Eğitmenin puan onayı (backend/score_review.py). Yalnız eğitmen panelinde ve
  // kohort öğrencisinin (eğitmenin görüş penceresindeki) raporunda gelir.
  score_review?: ScoreReview;
};
export type SessionIntent = { skill: string | null; goal: string | null; method: string | null };
export type IntentGoalStatus = "met" | "partial" | "not_met";
export type FidelityStatus = "present" | "partial" | "absent" | "no_opportunity";
export type IntentReview = {
  goal?: { status: IntentGoalStatus; note: string; turns: number[] };
  method?: {
    key: string;
    label?: string;
    drift: string;
    items: { key: string; label?: string; status: FidelityStatus; note: string; turns: number[] }[];
  };
};
// Niyet kartının verisi (`GET /sessions/{id}/intent`): yöntem adları isteğin dilinde.
export type IntentView = {
  intent: SessionIntent | null;
  locked: boolean;
  suggestion: string | null;
  skills: string[];
  methods: { key: string; label: string; family_label: string }[];
};
// Süpervizör rubriği (`backend/rubric_texts.rubric_for`): her maddenin 0'dan 5'e bant
// betimlemeleri, indeks = bant. Modelin puanlarken okuduğu metnin kendisi (ya da çevirisi).
export type SessionRubric = {
  language: string;
  rubric_version: number;
  skills: { skill: string; items: { key: string; bands: string[] }[] }[];
};
export type Session = { id: number; patient_case_id: number; patient_name?: string | null; started_at: string; ended_at: string | null; supervisor_report: SupervisorReport | null; reset_cycle: number; transcript?: TranscriptMessage[] | null; notes?: string | null; hint_limit?: number | null; hint_upgrade_available?: boolean | null; shared_with_instructors?: boolean };
export type HintResponse = { role: "hint"; content: string; timestamp: string; hints_used: number; hint_limit: number };
export type TokenResponse = { access_token: string; user_id: number; name: string };
export type SubscriptionStatus = {
  tier: "free" | "basic" | "pro";
  // Paid tiers: monthly counter. Free: null (its counter never refills).
  sessions_used_this_month: number | null;
  // Monthly limit on paid tiers; the LIFETIME limit on free.
  sessions_limit: number | null;
  // Free only: lifetime sessions used and the one client the account is bound to
  // (null until the first session actually happened). Both null on paid tiers.
  sessions_used_total: number | null;
  free_case_id: number | null;
  free_case_limit: number | null;
  // Paid tiers only: end of the paid period (ISO 8601) and whether the user has
  // already cancelled and is waiting it out. Both empty on free.
  current_period_end?: string | null;
  cancel_pending?: boolean;
};
// 402 reasons the server answers with when the free trial is over; the app routes
// to /pricing?reason=<code> and the pricing page shows the matching notice.
export type FreeBlockReason = "free_sessions_exhausted" | "free_case_locked";
// Fiyatlandırma sayfasının gösterebileceği tüm sebepler: free kapıları +
// ücretli tier'ın aylık hakkının bitmesi (ödünç Pro dahil, sunucu artık
// bunu kodlu döndürüyor).
export type PricingReason = FreeBlockReason | "monthly_sessions_exhausted" | "voice_requires_paid";
export function freeBlockReason(status: SubscriptionStatus | null, caseId: number): FreeBlockReason | null {
  if (!status || status.tier !== "free") return null;
  const used = status.sessions_used_total ?? 0;
  const limit = status.sessions_limit ?? 3;
  if (used >= limit) return "free_sessions_exhausted";
  if (status.free_case_id != null && status.free_case_id !== caseId) return "free_case_locked";
  return null;
}
export type Currency = "EUR" | "USD" | "TRY";
// "semester" = dört aylık akademik dönem (iyzico'da 4 ayda bir tahsilat).
export type BillingInterval = "monthly" | "semester" | "yearly";
// One plan in the catalog. Amounts are MINOR units of `currency` (cent / kuruş)
// and come from the real per-currency price list — never from a conversion.
// `yearly_price` is null when the plan has no annual price at all;
// `semester_price` is null unless the owner priced the term explicitly.
export type Plan = {
  name: string;
  currency: Currency;
  monthly_price: number;
  semester_price: number | null;
  yearly_price: number | null;
  limits: Record<string, number | boolean>;
};
// The catalog is answered in ONE currency, the one the account country implies:
// TR accounts get the TRY list (amounts KDV included), everyone else gets EUR.
export type PlanCatalog = {
  currency: Currency;
  country_code: string;
  country_source: string;
  country_locked: boolean;
  is_domestic: boolean;
  tax_included: boolean;
  vat_rate: number;
  plans: Plan[];
};
// The account country itself: the anchor the price hangs on. Locked after the
// first payment, after which only an admin can move it.
export type AccountCountry = {
  country_code: string;
  source: string;
  locked: boolean;
  currency: Currency;
  is_domestic: boolean;
  vat_rate: number;
  tax_included: boolean;
};
// iyzico checkout (shadow). When the integration is off, `enabled` is false and
// the pricing page keeps its "coming soon" behaviour.
export type CheckoutResponse = {
  enabled: boolean;
  detail?: string;
  token?: string;
  checkoutFormContent?: string;
  tokenExpireTime?: number;
  currency?: Currency;
  amount_minor?: number;
};
export type CheckoutResult = {
  enabled: boolean;
  status?: string;
  tier?: string | null;
  current_period_end?: string | null;
  detail?: string;
};
// Invoice/billing details captured on the payment screen. `country_code` (ISO-2)
// is what tells a domestic (TR) sale from a tax-exempt export.
export type BillingProfile = {
  buyer_type: "individual" | "company";
  full_name: string;
  company_name: string;
  country: string;
  country_code: string;
  city: string;
  address: string;
  zip_code: string;
  phone: string;
  identity_number: string;
  tax_office: string;
  tax_id: string;
  email: string;
};
export type SkillKey = "empathy" | "reflection" | "question_quality" | "focus" | "rapport";
export type ProgressPoint = {
  session_id: number;
  case_id: number;
  case_name: string;
  ended_at: string | null;
  overall: number;
  skills: Partial<Record<SkillKey, number>>;
};
export type PatientProgress = {
  case_id: number;
  name: string;
  age: number;
  alliance: number;
  session_count: number;
  insights: number;
  progress_map: Record<string, { stage: string; labels: Record<string, string> }>;
  wellbeing: number | null;
  wellbeing_trend: { value: number; ended_at: string | null }[];
};
export type Progress = {
  overall: {
    trend: ProgressPoint[];
    early_avg: Partial<Record<SkillKey, number>> | null;
    recent_avg: Partial<Record<SkillKey, number>> | null;
    latest_overall: number | null;
    band: "beginning" | "developing" | "competent" | "strong" | "exceptional" | null;
  };
  stats: {
    evaluated_sessions: number;
    patients_count: number;
    practice_minutes: number;
    best_skill: SkillKey | null;
    weakest_skill: SkillKey | null;
  };
  patients: PatientProgress[];
};
export type CompetencyBand = "beginning" | "developing" | "competent" | "strong" | "exceptional";
// ── Case Card (Vaka Kartı) ───────────────────────────────────────────────────
// The shareable summary of the work with ONE patient. `payload` is the whole card:
// the public page renders exactly this object, frozen at share time, so later
// sessions never leak through an old link. It never carries transcript or
// supervisor prose — only counts, scores and the alliance arc.
export type CardPayload = {
  version: number;
  patient: { name: string; age: number };
  sessions: number;
  evaluated_sessions: number;
  skills: Partial<Record<SkillKey, number>>;
  overall: number | null;
  band: CompetencyBand | null;
  most_improved: { skill: SkillKey; delta: number } | null;
  alliance: { start: number; current: number; delta: number; trail: number[] };
  student_name: string | null;
  locale: string;
  generated_at: string;
};
export type CardShare = {
  id: number;
  slug: string;
  url: string;
  locale: string;
  show_name: boolean;
  patient_case_id: number;
  created_at: string;
  payload: CardPayload;
};
export type CardPreview = { payload: CardPayload; share: CardShare | null };
export type PublicCard = { slug: string; locale: string; created_at: string; payload: CardPayload };

// Public card fetch used by the server-rendered /card/[slug] page: no token, no
// localStorage (which `request` reaches for), and never cached — a revoked card
// has to stop resolving immediately.
export async function fetchPublicCard(slug: string): Promise<PublicCard | null> {
  // On the server the public API hostname may not be reachable from inside the
  // container (it would have to hairpin through the proxy), so an optional
  // runtime-only API_INTERNAL_URL wins there. In the browser it is undefined and
  // the normal base is used.
  const base = process.env.API_INTERNAL_URL || BASE;
  try {
    const res = await fetch(`${base}/cards/public/${encodeURIComponent(slug)}`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as PublicCard;
  } catch {
    return null;
  }
}

export type NotificationPrefs = { session_feedback: boolean; product_updates: boolean };
// "Invite a Friend" — the user's referral code + shareable link + reward stats.
export type Referral = {
  code: string;
  link: string;
  invited_count: number;
  rewarded_count: number;
  bonus_active: boolean;
  bonus_pro_until: string | null;
  reward_days: number;
};
// ── Kurumsal katman: kurum → kohort → koltuk ─────────────────────────────────
// The registration form has ONE code field; `lookupCohort` is what tells a cohort
// invite code from a referral code. `found: false` is a normal answer, not an error.
export type CohortLookup = {
  found: boolean;
  cohort_id?: number;
  code?: string;
  name?: string;
  organization_name?: string;
  instructor_name?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  seat_limit?: number;
  seats_taken?: number;
  seats_available?: number | null;
  joinable?: boolean;
  reason?: "full" | "ended" | "inactive" | null;
};
export type CohortMembership = {
  member: boolean;
  active?: boolean;
  cohort_id?: number;
  cohort_name?: string;
  organization_name?: string;
  instructor_name?: string | null;
  joined_at?: string | null;
  ends_at?: string | null;
};
export type InstructorRole = "owner" | "co_instructor" | "assistant";
export type InstructorMe = {
  instructor: boolean;
  name?: string;
  cohort_id?: number;
  cohort_name?: string;
  open?: boolean;
  // Bu kohorttaki rol: sahip, eş eğitmen ya da asistan (backend `_pick_cohort`).
  role?: InstructorRole;
  // Kişinin açabileceği bütün kohortlar (birden çoksa kabuk seçici gösterir).
  cohorts?: { id: number; name: string; role: InstructorRole; open: boolean }[];
  // Aynı hesap öğrenci alanında da kullanılmış mı (hesap geçişini gösterir).
  has_personal_account?: boolean;
  // Rehberli turu bu hesapta kendiliğinden başlamış sayfalar (panel | student | session).
  tours_seen?: string[];
  // Herkese açık, salt okunur demo paneli (backend/demo_cohort.py).
  demo?: boolean;
};
// Sahibin eklediği eş eğitmen / asistan (backend/routers/instructor.py `_staff_row`).
export type InstructorStaffMember = {
  id: number;
  email: string;
  role: "co_instructor" | "assistant";
  status: "pending" | "accepted" | "expired";
  name: string | null;
  email_sent: boolean;
  created_at: string | null;
  accepted_at: string | null;
  expires_at: string | null;
};
export type InstructorStaffList = { limit: number; used: number; staff: InstructorStaffMember[] };
export type InstructorStaffSendResponse = InstructorStaffList & { email_sent: boolean; link: string; id: number };
export type InstructorStudentRow = {
  student_id: number;
  name: string;
  email: string;
  joined_at: string | null;
  left_at: string | null;
  status: string;
  sessions_count: number;
  evaluated_count: number;
  latest_overall: number | null;
  band: string | null;
  // Son değerlendirilen seans − aynı süreçte bir önceki (seans ekranındaki ▲ ile aynı).
  change: number | null;
  last_session_at: string | null;
  started: boolean;
  // "Takip gerekenler" nedenleri: hiç başlamadı / 7 gündür seans yok / son iki seansta puan düştü.
  follow_up: ("not_started" | "inactive" | "score_drop")[];
  // Hatırlat düğmesi bu satırda gösterilir mi (hiç başlamadı ya da 7 gündür seans yok).
  remind_eligible: boolean;
  last_reminded_at: string | null;
  // Son hatırlatmadan bu yana geçen tam gün (0 = bugün); hiç yoksa null.
  reminded_days_ago: number | null;
  // Doluysa bir sonraki hatırlatmanın açılacağı an (3 günde en fazla bir).
  remind_available_at: string | null;
};
export type InstructorOverview = {
  cohort: {
    id: number; name: string; organization_name: string;
    starts_at: string | null; ends_at: string | null;
    seat_limit: number; seats_taken: number; seats_available: number | null;
  };
  stats: {
    students: number; not_started: number; total_sessions: number;
    evaluated_students: number; cohort_avg: number | null;
  };
};
export type InstructorStudentDetail = {
  student: { id: number; name: string; joined_at: string | null; status: string };
  window: { start: string | null; end: string | null };
  summary: {
    sessions_count: number; evaluated_count: number;
    latest_overall: number | null; band: string | null;
  };
  trend: { session_id: number; ended_at: string | null; overall: number; approved?: boolean; skills: Partial<Record<SkillKey, number>> }[];
  sessions: {
    session_id: number; case_id: number; reset_cycle: number; case_name: string;
    started_at: string | null; ended_at: string | null;
    message_count: number; has_report: boolean; overall: number | null;
    review_status?: ScoreReview["status"] | null;
  }[];
  // {vaka id: canlı sürecin numarası}; seansın reset_cycle'ı bundan küçükse
  // seans bitmiş bir sürece aittir (katlanmış gösterilir).
  processes: Record<string, number>;
  custom_cases: {
    id: number; name: string; age: number; presenting_complaint: string;
    translations?: CaseTranslations; difficulty_level: string; created_at: string | null;
  }[];
};
export type InstructorSessionDetail = {
  session_id: number;
  case_name: string;
  case_complaint: string | null;
  case_complaint_translations?: CaseTranslations;
  started_at: string | null;
  ended_at: string | null;
  transcript: TranscriptMessage[];
  supervisor_report: SupervisorReport | null;
  // Sahip ve eş eğitmen puanları onaylayabilir; asistan ve demo yalnızca görür.
  can_review?: boolean;
  // Terapistin hastada iz bırakan cümleleri; yalnızca eğitmene gider.
  remembered_lines?: RememberedLine[];
};
export type RememberedLine = {
  turn: number | null;
  quote: string;
  valence: "positive" | "negative";
  note: string;
};
// Faz 4: kohort × vaka ödevi (backend/routers/instructor.py `_assignment_row`).
export type InstructorAssignment = {
  id: number;
  patient_case_id: number;
  case_name: string;
  case_names: CaseTranslations;
  // Kohortta başlayanların hepsi aynı dile kilitliyse o dil (Karar 20); ad seans ekranlarıyla aynı olsun.
  case_locked_locale?: string | null;
  case_age: number | null;
  case_is_own: boolean;
  title: string | null;
  // Eğitmenin serbest metin talimatı (öğrenci vakanın başında görür); yoksa null.
  instructions: string | null;
  starts_at: string | null;
  ends_at: string | null;
  reset_allowed: boolean;
  // Bitişe 3 gün kala, o ödevde seansı olmayan öğrenciye otomatik e-posta (varsayılan açık).
  due_reminder?: boolean;
  is_active: boolean;
  live: boolean;
  students_started: number;
  created_at: string | null;
};
export type InstructorAssignableCase = {
  id: number; name: string; names: CaseTranslations; age: number; is_own: boolean; assigned: boolean;
};
export type InstructorAssignmentList = { assignments: InstructorAssignment[]; cases: InstructorAssignableCase[] };
export type InstructorAssignmentInput = {
  patient_case_id: number; title?: string | null; instructions?: string | null; starts_at?: string | null; ends_at?: string | null; reset_allowed?: boolean; due_reminder?: boolean;
};
export type InstructorAssignmentGrid = {
  assignment: InstructorAssignment;
  weeks: number;
  students: {
    student_id: number; name: string; status: string; current_cycle: number | null;
    processes: {
      cycle: number; current: boolean;
      sessions: { session_id: number; index: number | null; ended_at: string | null; overall: number | null; weather: string | null }[];
    }[];
  }[];
};
export type InstructorInvitation = {
  valid: boolean;
  email?: string;
  cohort_name?: string;
  organization_name?: string;
  has_account?: boolean;
  // Yalnızca eş eğitmen / asistan davetinde (`/instructor/staff-invitation`).
  role?: "co_instructor" | "assistant";
  inviter_name?: string | null;
};

// Kapalı anahtar kümeleri backend/routers/instructor.py ile elle senkron.
export const APPLICATION_AUDIENCES = [
  "undergraduate", "masters", "doctoral", "clinical_training", "early_career", "other",
] as const;
export const APPLICATION_TIMEFRAMES = ["now", "this_term", "next_term", "undecided"] as const;

export type InstructorApplicationInput = {
  full_name: string;
  email: string;
  phone?: string;
  organization_name: string;
  country_code?: string;
  title?: string;
  audience: (typeof APPLICATION_AUDIENCES)[number];
  program?: string;
  student_count: number;
  start_timeframe?: (typeof APPLICATION_TIMEFRAMES)[number];
  message?: string;
  locale?: string;
  website?: string;
};

export type GuideSection = { heading: string; body: string; bullets: string[] };
export type GuideContent = { title: string; intro: string; sections: GuideSection[]; footer: string };
export type Guide = { locale: string; content: GuideContent; source: string; generated_at: string | null };

// Ziyaretçinin IP'sinden çözülen ülke ve o ülkenin başlangıç dili. Yalnızca
// kayıtlı bir dil seçimi (NEXT_LOCALE cookie'si) YOKKEN sorulur (LocaleSync).
export type GeoHint = { country_code: string | null; locale: string };

export const api = {
  geo: () => request<GeoHint>("/geo"),
  register: (email: string, name: string, password: string, locale?: string, referral_code?: string) =>
    request<TokenResponse>("/auth/register", { method: "POST", body: JSON.stringify({ email, name, password, locale, referral_code }) }),

  // Şifre değişince eski oturumların hepsi düşer (A9); yanıt bu sekme için taze token taşır.
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: boolean; access_token?: string }>("/auth/password", { method: "PUT", body: JSON.stringify({ current_password, new_password }) }),

  // `consent_accepted` = kullanıcı hukuki metinleri HİÇ kabul etti mi (eski alan).
  // Kapıyı belirleyen alan `consent_current`: kabul edilen sürüm (`consent_version`)
  // yürürlükteki `legal_version` kadar yeni mi. İkisi birlikte "metin güncellendi"
  // durumunu ayırt etmeyi sağlar (accepted && !current).
  getMe: () =>
    request<{ id: number; name: string; email: string; consent_accepted: boolean; consent_version: string | null; legal_version: string; consent_current: boolean; email_verified: boolean }>("/auth/me"),

  verifyEmail: (code: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email", { method: "POST", body: JSON.stringify({ code }) }),

  // Maildeki tek-tık doğrulama linki. Public uç: oturum gerekmez (kullanıcı linki
  // başka bir cihazda açmış olabilir), token'ın kendisi imzalıdır.
  verifyEmailLink: (token: string) =>
    request<{ ok: boolean; already_verified?: boolean; email?: string }>("/auth/verify-email/link", { method: "POST", body: JSON.stringify({ token }) }),

  resendVerification: (locale?: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email/resend", { method: "POST", body: JSON.stringify({ locale }) }),

  forgotPassword: (email: string, locale?: string) =>
    request<{ ok: boolean }>("/auth/password/forgot", { method: "POST", body: JSON.stringify({ email, locale }) }),

  resetPassword: (token: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password/reset", { method: "POST", body: JSON.stringify({ token, new_password }) }),

  acceptConsent: (locale?: string, marketing?: boolean) =>
    request<{ ok: boolean }>("/auth/consent/accept", { method: "POST", body: JSON.stringify({ locale, marketing }) }),

  // NOT: `declineConsent` kaldırıldı. Onay akışında reddetme yoktur ve reddetmek
  // hesabı silmez; kabul etmeyen kullanıcı onay ekranında kalır. Hesabını silmek
  // isteyen aşağıdaki mail kodlu silme akışını kullanır.

  requestAccountDeletion: (locale?: string) =>
    request<{ ok: boolean }>("/auth/account/delete/request", { method: "POST", body: JSON.stringify({ locale }) }),

  confirmAccountDeletion: (code: string) =>
    request<{ ok: boolean }>("/auth/account/delete/confirm", { method: "POST", body: JSON.stringify({ code }) }),

  login: (email: string, password: string) =>
    request<TokenResponse>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),

  // create_account=false: yalnızca var olan hesaba giriş (eğitmen girişi) — adrese
  // ait hesap yoksa sessizce öğrenci hesabı açılmaz, 404 + `no_account` döner.
  googleAuth: (credential: string, referral_code?: string, create_account = true) =>
    request<TokenResponse>("/auth/google", { method: "POST", body: JSON.stringify({ credential, referral_code, create_account }) }),

  getReferral: () => request<Referral>("/referral"),

  getCases: () => request<Case[]>("/cases"),
  getCase: (caseId: number) => request<Case>(`/cases/${caseId}`),

  getGuide: (locale: string) => request<Guide>(`/guide?locale=${encodeURIComponent(locale)}`),

  startSession: (caseId: number) =>
    // Karar 20: öğrencinin dili İLK seansta hastaya kilitlenir (isimler/kadro bu
    // dilde çözülür ve dil sonra değişse bile sabit kalır). Cookie yoksa backend
    // İngilizce kilitler.
    request<Session>("/sessions", {
      method: "POST",
      body: JSON.stringify({ patient_case_id: caseId, locale: currentLocale() }),
    }),

  // Yalnızca SSE yolu kullanılır; bloklayan `POST /message` istemciden çağrılmaz (F12).
  streamMessage,
  streamVoiceMessage,

  getVoiceAvailability: (sessionId: number) =>
    request<VoiceAvailability>(`/sessions/${sessionId}/voice`),

  requestHint: (sessionId: number, locale = "en") =>
    request<HintResponse>(`/sessions/${sessionId}/hint?language=${encodeURIComponent(locale)}`, { method: "POST" }),

  endSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}/end`, { method: "POST" }),

  getSessionIntent: (sessionId: number) =>
    request<IntentView>(`/sessions/${sessionId}/intent`),

  saveSessionIntent: (sessionId: number, intent: SessionIntent) =>
    request<{ intent: SessionIntent }>(`/sessions/${sessionId}/intent`, {
      method: "PUT",
      body: JSON.stringify(intent),
    }),

  updateSessionNotes: (sessionId: number, content: string) =>
    request<{ ok: boolean }>(`/sessions/${sessionId}/notes`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  getSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}`),

  // Kicks off the supervisor evaluation as a BACKGROUND job on the server and
  // returns immediately; poll getEvaluationStatus (or just re-call this — it's a
  // no-op while running) until "ready", then load the report via getSession.
  evaluateSession: (sessionId: number, locale = "en") =>
    request<EvaluationStatus>(`/sessions/${sessionId}/evaluate?language=${encodeURIComponent(locale)}`, { method: "POST" }),

  getEvaluationStatus: (sessionId: number) =>
    request<EvaluationStatus>(`/sessions/${sessionId}/evaluation-status`),

  // Rubrik tablosu: madde × bant 0-5 betimlemeleri, raporun yazıldığı dilde.
  getSessionRubric: (sessionId: number) =>
    request<SessionRubric>(`/sessions/${sessionId}/rubric`),

  getUnreadReports: () =>
    request<UnreadReports>("/sessions/unread-reports"),

  // Idempotent: called when the report is actually on screen, so a remount or a
  // second tab repeating it is a no-op server-side.
  markReportSeen: (sessionId: number) =>
    request<{ ok: boolean; marked: boolean }>(`/sessions/${sessionId}/report-seen`, { method: "POST" }),

  getCaseState: (caseId: number) =>
    // `session_count` süpervizör raporu yazılınca artar (hastanın promptu bunu
    // kullanır); dashboard ise raporu beklemeden "bu danışanla seans yaptım"
    // diyebilmek için `completed_sessions`'ı okur.
    request<{ session_count: number; completed_sessions: number; last_session_at: string | null }>(`/cases/${caseId}/state`),

  resetCaseState: (caseId: number) =>
    request<{ ok: boolean }>(`/cases/${caseId}/reset-state`, { method: "POST" }),

  getSessionsByCase: (caseId: number) =>
    request<Session[]>(`/sessions/by-case/${caseId}`),

  getProgress: (lang?: string) =>
    request<Progress>(`/sessions/progress${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`),

  getSubscriptionStatus: () =>
    request<SubscriptionStatus>("/subscription/status"),

  getPlans: () =>
    request<PlanCatalog>("/subscription/plans"),

  // The server decides the currency from the account country; `currency` is sent
  // only so an older payload shape stays valid, and is ignored server-side.
  startCheckout: (plan: string, currency: Currency = "EUR", interval: BillingInterval = "monthly") =>
    request<CheckoutResponse>("/payments/subscription/checkout", {
      method: "POST",
      body: JSON.stringify({ plan, currency, interval }),
    }),

  getCheckoutResult: (token: string) =>
    request<CheckoutResult>("/payments/subscription/checkout/result", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  // `{enabled:false}` ödeme kapalı; `status`: canceled | no_active_subscription.
  cancelSubscription: () =>
    request<{ enabled: boolean; status?: "canceled" | "no_active_subscription" }>("/payments/subscription/cancel", { method: "POST" }),

  getBillingProfile: () =>
    request<BillingProfile>("/payments/billing-profile"),

  saveBillingProfile: (profile: Partial<BillingProfile>) =>
    request<BillingProfile>("/payments/billing-profile", { method: "PUT", body: JSON.stringify(profile) }),

  getAccountCountry: () =>
    request<AccountCountry>("/payments/account-country"),

  saveAccountCountry: (country_code: string) =>
    request<AccountCountry>("/payments/account-country", {
      method: "PUT",
      body: JSON.stringify({ country_code }),
    }),

  // Karar 40: üretim arka planda koşar — POST hemen döner, hazır olunca
  // getGenerationStatus "ready" verir ve PendingGenerationWatcher bildirimi düşürür.
  createCase: (body: CreateCaseRequest) =>
    request<{ status: string }>("/cases", { method: "POST", body: JSON.stringify(body) }),

  getGenerationStatus: () =>
    request<{ status: "none" | "running" | "ready" | "failed"; case_id?: number; name?: string | null }>(
      "/cases/generation-status"
    ),

  // Live card numbers for the Share dialog. Nothing becomes public here — only
  // shareCard() creates a page.
  getCardPreview: (caseId: number, locale: string, showName: boolean) =>
    request<CardPreview>(`/cards/preview/${caseId}?locale=${encodeURIComponent(locale)}&show_name=${showName}`),

  shareCard: (patient_case_id: number, locale: string, show_name: boolean) =>
    request<CardShare>("/cards/share", {
      method: "POST",
      body: JSON.stringify({ patient_case_id, locale, show_name }),
    }),

  getCardShares: () => request<CardShare[]>("/cards/shares"),

  revokeCardShare: (shareId: number) =>
    request<{ ok: boolean }>(`/cards/shares/${shareId}`, { method: "DELETE" }),
  // ── Kohort (kurumsal katman) ───────────────────────────────────────────────
  lookupCohort: (code: string) =>
    request<CohortLookup>(`/cohorts/lookup?code=${encodeURIComponent(code)}`),

  // Only ever called from the separate cohort-consent screen — the explicit
  // instructor-access consent is part of the request body, not an implicit side
  // effect of typing a code.
  joinCohort: (code: string, locale?: string) =>
    request<{ ok: boolean; already_member: boolean; membership: CohortMembership }>("/cohorts/join", {
      method: "POST",
      body: JSON.stringify({ code, accept_instructor_access: true, locale }),
    }),

  // ── Eğitmen alanı ──────────────────────────────────────────────────────────
  getInstructorMe: () => request<InstructorMe>("/instructor/me"),
  openInstructorDemo: (lang: string) =>
    request<{ access_token: string; locale: string }>(
      `/instructor/demo?lang=${encodeURIComponent(lang)}`, { method: "POST" }),

  getInstructorOverview: () => request<InstructorOverview>("/instructor/overview"),

  getInstructorStudents: () =>
    request<{ students: InstructorStudentRow[] }>("/instructor/students"),

  // Kohort panelinin .xlsx dökümü; başlıklar `lang` dilinde, anlar `tz` saatinde.
  downloadInstructorExport: (lang: string, tz: string) =>
    requestBlob(`/instructor/export?lang=${encodeURIComponent(lang)}&tz=${encodeURIComponent(tz)}`),

  getInstructorStudent: (studentId: number) =>
    request<InstructorStudentDetail>(`/instructor/students/${studentId}`),

  getInstructorSession: (studentId: number, sessionId: number) =>
    request<InstructorSessionDetail>(`/instructor/students/${studentId}/sessions/${sessionId}`),

  // Puan kartında "Onayla": değişen beceriler + isteğe bağlı gerekçe.
  reviewInstructorSession: (studentId: number, sessionId: number,
                            scores: Partial<Record<SkillKey, number>>, reasons: Partial<Record<SkillKey, string>>) =>
    request<{ supervisor_report: SupervisorReport }>(`/instructor/students/${studentId}/sessions/${sessionId}/review`, {
      method: "PUT", body: JSON.stringify({ scores, reasons }),
    }),

  // "Hatırlat": öğrenciye hocanın adıyla e-posta + uygulamada şerit (3 günde en fazla bir).
  remindInstructorStudent: (studentId: number) =>
    request<{ last_reminded_at: string | null; reminded_days_ago: number | null; remind_available_at: string | null; email_sent: boolean }>(
      `/instructor/students/${studentId}/remind`, { method: "POST" }),

  // Öğrenci uygulamasının üst şeridi: eğitmenin gönderdiği, kapatılmamış hatırlatmalar.
  getMyReminders: () => request<{ reminders: StudentReminderNotice[] }>("/cohorts/me/reminders"),
  dismissMyReminder: (id: number) =>
    request<{ ok: boolean }>(`/cohorts/me/reminders/${id}/dismiss`, { method: "POST" }),

  readInstructorInvitation: (token: string) =>
    request<InstructorInvitation>(`/instructor/invitation?token=${encodeURIComponent(token)}`),

  // Hesabı olmayan için şifre + ad; hesabı olan için şifre YOK — çağrı giriş
  // yapılmış oturumun token'ıyla gider ve rol o hesaba eklenir.
  acceptInstructorInvitation: (token: string, password?: string, name?: string) =>
    request<TokenResponse>("/instructor/invitation/accept", {
      method: "POST",
      body: JSON.stringify({ token, password, name }),
    }),

  // Eş eğitmen / asistan daveti: kabul ekranı eğitmen davetiyle aynı (`?staff=1`).
  readStaffInvitation: (token: string) =>
    request<InstructorInvitation>(`/instructor/staff-invitation?token=${encodeURIComponent(token)}`),
  acceptStaffInvitation: (token: string, password?: string, name?: string) =>
    request<TokenResponse & { cohort_id: number }>("/instructor/staff-invitation/accept", {
      method: "POST",
      body: JSON.stringify({ token, password, name }),
    }),
  getInstructorStaff: () => request<InstructorStaffList>("/instructor/staff"),
  inviteInstructorStaff: (email: string, role: "co_instructor" | "assistant", locale: string) =>
    request<InstructorStaffSendResponse>("/instructor/staff", {
      method: "POST",
      body: JSON.stringify({ email, role, locale }),
    }),
  resendInstructorStaff: (id: number, locale: string) =>
    request<InstructorStaffSendResponse>(`/instructor/staff/${id}/resend`, {
      method: "POST",
      body: JSON.stringify({ locale }),
    }),
  markInstructorTourSeen: (tour: string) =>
    request<{ ok: boolean }>(`/instructor/tours/${tour}/seen`, { method: "POST" }),
  removeInstructorStaff: (id: number) =>
    request<InstructorStaffList>(`/instructor/staff/${id}`, { method: "DELETE" }),

  submitInstructorApplication: (body: InstructorApplicationInput) =>
    request<{ ok: boolean }>("/instructor/applications", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getNotificationPrefs: () =>
    request<NotificationPrefs>("/notifications/preferences"),

  updateNotificationPrefs: (prefs: NotificationPrefs) =>
    request<NotificationPrefs>("/notifications/preferences", { method: "PUT", body: JSON.stringify(prefs) }),

  // ── Eğitmenin öğrenci davetleri ────────────────────────────────────────────
  // Davet KİŞİYE ÖZEL ve TEK KULLANIMLIKTIR; `link` her satırda döner çünkü mail
  // gitmediğinde eğitmenin linki elden iletebilmesi bilinçli bir karardır.
  getCohortInvitations: () =>
    request<CohortInvitationList>("/instructor/invitations"),

  // `locale` gönderilmezse davet maili İngilizce gider — arayüz dilini taşımak
  // bu yüzden opsiyonel değil, pratikte zorunlu.
  sendCohortInvitations: (emails: string[], locale?: string) =>
    request<CohortInviteSendResponse>("/instructor/invitations", {
      method: "POST",
      body: JSON.stringify({ emails, locale }),
    }),

  // Yeniden gönderim YENİ token üretir, eskisini geçersiz kılar. Kabul edilmiş
  // davette 409 + `already_accepted` döner.
  resendCohortInvitation: (invitationId: number, locale?: string) =>
    request<{ ok: boolean; email_sent: boolean; link: string }>(
      `/instructor/invitations/${invitationId}/resend`,
      { method: "POST", body: JSON.stringify({ locale }) },
    ),

  revokeCohortInvitation: (invitationId: number) =>
    request<{ ok: boolean }>(`/instructor/invitations/${invitationId}`, { method: "DELETE" }),

  removeCohortStudent: (studentId: number) =>
    request<{ ok: boolean }>(`/instructor/students/${studentId}`, { method: "DELETE" }),

  // Paylaşımlı (kişiye özel OLMAYAN) kohort linki — ikincil yol.
  getCohortShareLink: () => request<CohortShareLink>("/instructor/invite-link"),

  // Kohort ödevleri (Faz 4).
  getInstructorAssignments: () => request<InstructorAssignmentList>("/instructor/assignments"),
  createInstructorAssignment: (body: InstructorAssignmentInput) =>
    request<InstructorAssignment>("/instructor/assignments", { method: "POST", body: JSON.stringify(body) }),
  updateInstructorAssignment: (id: number, body: Partial<Omit<InstructorAssignmentInput, "patient_case_id">> & { is_active?: boolean; due_reminder?: boolean }) =>
    request<InstructorAssignment>(`/instructor/assignments/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteInstructorAssignment: (id: number) =>
    request<{ ok: boolean }>(`/instructor/assignments/${id}`, { method: "DELETE" }),
  getInstructorAssignment: (id: number) =>
    request<InstructorAssignmentGrid>(`/instructor/assignments/${id}`),

  // Kayıt sayfasının kişiye özel daveti tanıması için public uç (auth yok).
  readCohortInviteToken: (token: string) =>
    request<CohortInviteToken>(`/cohorts/invitation?token=${encodeURIComponent(token)}`),

  // Aynı /cohorts/join ucu, kodun yerine tek kullanımlık token ile.
  joinCohortByInvite: (invite_token: string, locale?: string) =>
    request<{ ok: boolean; already_member: boolean; membership: CohortMembership }>("/cohorts/join", {
      method: "POST",
      body: JSON.stringify({ invite_token, accept_instructor_access: true, locale }),
    }),
};

// ── Eğitmen davet tipleri ────────────────────────────────────────────────────
export type CohortInviteStatus = "pending" | "accepted" | "revoked";
export type CohortInvitationRow = {
  id: number;
  email: string;
  status: CohortInviteStatus;
  email_sent: boolean;
  sent_at: string | null;
  accepted_at: string | null;
  accepted_name: string | null;
  link: string;
};
export type CohortInvitationList = {
  seat_limit: number;
  seats_taken: number;
  seats_available: number | null;
  invitations: CohortInvitationRow[];
};
// `resent` yalnızca "tekrar gönderildi" demek değildir: İPTAL EDİLMİŞ bir adres
// yeniden davet edilince de satır yeniden kullanılır ve sonuç `resent` olur.
export type CohortInviteOutcome =
  | "created" | "resent" | "invalid" | "duplicate_in_input" | "already_accepted";
export type CohortInviteResult = {
  email: string;
  outcome: CohortInviteOutcome;
  email_sent: boolean;
  // Mailler arka planda tek oturumla gider (A13): `queued` = yola çıkacak,
  // gerçek sonuç davet listesindeki `email_sent`e düşer; `disabled` = SMTP kapalı.
  email_status?: "queued" | "disabled";
  link: string | null;
  id: number | null;
};
export type CohortInviteSendResponse = {
  seat_limit: number;
  seats_taken: number;
  seats_available: number | null;
  results: CohortInviteResult[];
};
export type CohortShareLink = { code: string; url: string };
// Token seviyesindeki hatalarda (`not_found`/`used`/`revoked`/`expired`) hem
// `cohort` hem `email` null gelir. Token geçerli ama kohort kapalıysa
// (`full`/`ended`/`inactive`) `valid: false` olmasına rağmen `cohort` DOLUDUR —
// davetli kişi neyi kaçırdığını görebilsin diye.
export type CohortInviteToken = {
  valid: boolean;
  reason: null | "not_found" | "used" | "revoked" | "expired" | "full" | "ended" | "inactive";
  email: string | null;
  cohort: {
    id: number;
    name: string;
    organization_name: string;
    instructor_name: string | null;
    seat_limit: number;
    seats_taken: number;
    seats_available: number | null;
    ends_at: string | null;
  } | null;
};
